// Simulador de "cuánto paga una entrega" con la configuración que el operador está
// editando. Matemática PURA, sin React.
//
// ── Por qué existe ──────────────────────────────────────────────────────────────────
// La cascada de tarifación es la parte de esta pantalla que más texto necesitaba para
// explicarse (base → ajuste por comuna → recargo por tamaño → IVA, con excepciones de
// pick & go y flotas). Mostrar el resultado sobre un caso concreto reemplaza esa
// explicación: en vez de leer cómo se combinan las reglas, el operador ve el número.
//
// ── Espejo del backend, y sus límites ───────────────────────────────────────────────
// Replica `driver-payment-rating.service.buildEffectiveRateCard` + `pricing-core
// .ratePackage` + `fleet-attribution.resolveAttribution` de microservice-billing. La
// entrada es EXACTAMENTE el payload que se va a guardar (la salida de `zonesToApi`,
// `vehicleRatesToApi`, etc.), no el estado interno del formulario: así el simulador
// depende del mismo contrato que el backend y no de cómo esté armada la UI.
//
// DOS cosas que el simulador NO sabe, y que la UI declara al usuario:
//   1. Los overrides POR REPARTIDOR (`DriverTariff`, que se editan en
//      /admin/billing/driver-tariffs). En el motor real le ganan a las reglas del
//      operador, así que un repartidor con tarifa propia puede cobrar otra cosa.
//   2. Las tarifas por código postal. Se omiten a propósito: solo aplican en países que
//      las habilitan y pedir un CP haría el simulador más complejo que el texto que
//      viene a reemplazar.
// Por eso el resultado se rotula como estimación de la config del OPERADOR.

/** Entero seguro (redondea; 0 si no es finito). Espeja `toInt` de pricing-core. */
const toInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : 0;
};

/**
 * Normaliza un nombre de comuna: minúsculas, sin acentos, espacios colapsados.
 * Espeja `normalizeCommune` de pricing-core — si divergen, el simulador diría que una
 * comuna tiene ajuste cuando el backend no lo encuentra (o al revés).
 */
export const normalizeCommune = (name) =>
  String(name ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/** Espeja `canonicalVehicleType`: 'suv' legacy colapsa a 'sedan'; desconocido → null. */
export const canonicalVehicleType = (raw) => {
  if (typeof raw !== 'string') return null;
  const v = raw.trim().toLowerCase();
  if (v === 'suv') return 'sedan';
  return ['sedan', 'furgo', 'camion', 'moto'].includes(v) ? v : null;
};

/**
 * Precio absoluto de una regla de comuna. Espeja `resolveZoneBaseCost`:
 * modelo relativo (+/− sobre la base, con piso 0) y passthrough del `baseCost` legacy.
 */
export const resolveZoneBaseCost = (zone, effectiveDefault) => {
  if (zone?.adjustmentType === 'add' || zone?.adjustmentType === 'subtract') {
    if (effectiveDefault == null) return null;
    const amount = Math.max(0, toInt(zone.amount));
    const signed = zone.adjustmentType === 'subtract' ? -amount : amount;
    return Math.max(0, toInt(effectiveDefault) + signed);
  }
  if (zone?.baseCost != null) return toInt(zone.baseCost);
  return null;
};

/**
 * Simula UNA entrega.
 *
 * @param config payload de la config (defaultBaseCost, paymentMode, vehicleRates,
 *   zones, sizeSurcharges, adjustPickAndGoByCommune, fleetLeaders, taxMode, taxRate)
 * @param scenario { communeName, vehicleType, sizeCategory, pickAndGo, fleetLeaderId }
 * @returns {{ unit, net, tax, total, priced, flat, steps }} — `steps` es el desglose
 *   legible (label + monto) que la UI pinta debajo de la cifra.
 */
export function simulateDelivery(config = {}, scenario = {}) {
  const {
    defaultBaseCost,
    paymentMode = 'single',
    vehicleRates = [],
    zones = [],
    sizeSurcharges = [],
    adjustPickAndGoByCommune = true,
    fleetLeaders = [],
    taxMode = 'exclusive',
    taxRate = 0.19,
  } = config;
  const {
    communeName = '',
    vehicleType = null,
    sizeCategory = null,
    pickAndGo = false,
    fleetLeaderId = null,
  } = scenario;

  const steps = [];

  // ── Excepción 1: flota tercerizada. Monto PLANO PURO: corta la cascada entera.
  if (fleetLeaderId) {
    const leader = fleetLeaders.find(
      (f) => String(f?.driverId) === String(fleetLeaderId),
    );
    const amount = leader ? toInt(leader.amountPerDelivery) : 0;
    if (amount > 0) {
      return {
        unit: amount,
        priced: true,
        flat: true,
        steps: [{ key: 'fleet', amount }],
        ...applyTax(amount, taxMode, taxRate),
      };
    }
    // Líder sin monto válido: no se paga $0 en silencio (espeja `fleetAmountFor`, que
    // trata un monto <= 0 como NO declarado y devuelve la entrega al motor normal).
  }

  const byVehicle = paymentMode === 'byVehicle';
  const vt = byVehicle ? canonicalVehicleType(vehicleType) : null;

  // ── Base por defecto: la tarifa del vehículo se INSERTA en el eslabón "default del
  // operador"; si el tipo no tiene tarifa propia (o el repartidor no tiene vehículo),
  // cae al monto por defecto global.
  const vehicleRate =
    byVehicle && vt != null
      ? vehicleRates.find((r) => r?.vehicleType === vt)?.baseCost ?? null
      : null;
  const rawDefault = vehicleRate ?? defaultBaseCost;
  if (rawDefault == null || !Number.isFinite(Number(rawDefault))) {
    return { unit: 0, net: 0, tax: 0, total: 0, priced: false, flat: false, steps: [] };
  }
  const effectiveDefault = toInt(rawDefault);
  steps.push({
    key: vehicleRate != null ? 'vehicleBase' : 'default',
    amount: effectiveDefault,
    vehicleType: vt,
  });

  // ── Excepción 2: pick & go con el ajuste por comuna apagado → se ignoran las zonas
  // (el recargo por tamaño SÍ se conserva).
  const ignoreZones = !adjustPickAndGoByCommune && pickAndGo;

  // ── Ajuste por comuna. En byVehicle, una zona específica del vehículo le gana a la
  // genérica de la misma comuna (por eso se recorren genéricas primero).
  let base = effectiveDefault;
  let matchedZone = null;
  if (!ignoreZones) {
    const key = normalizeCommune(communeName);
    if (key) {
      const isSpecific = (z) =>
        byVehicle && Array.isArray(z?.vehicleTypes) && z.vehicleTypes.length > 0;
      const matches = (z) => {
        if (!byVehicle) return true;
        if (!isSpecific(z)) return true;
        return vt != null && z.vehicleTypes.includes(vt);
      };
      const ordered = byVehicle
        ? [...zones.filter((z) => !isSpecific(z)), ...zones.filter(isSpecific)]
        : zones;
      for (const z of ordered) {
        if (!matches(z)) continue;
        const names = (z?.communeNames ?? []).map(normalizeCommune);
        if (!names.includes(key)) continue;
        const resolved = resolveZoneBaseCost(z, effectiveDefault);
        if (resolved != null) {
          base = resolved;
          matchedZone = z;
        }
      }
    }
  }
  if (matchedZone) {
    steps.push({
      key: 'zone',
      amount: base - effectiveDefault,
      adjustmentType: matchedZone.adjustmentType,
      absolute: matchedZone.adjustmentType ? null : base,
    });
  } else if (ignoreZones) {
    steps.push({ key: 'pickAndGoNoZone', amount: 0 });
  }

  // ── Recargo por tamaño: unit = round(base × multiplicador + fijo).
  const cat = String(sizeCategory ?? '').trim().toUpperCase();
  const surcharge = cat ? sizeSurcharges.find((s) => s?.category === cat) : undefined;
  const multiplier = Number(surcharge?.multiplier ?? 1) || 1;
  const additionalCost = toInt(surcharge?.additionalCost);
  const unit = Math.round(base * multiplier + additionalCost);
  if (cat) {
    steps.push({
      key: surcharge ? 'size' : 'sizeUnconfigured',
      category: cat,
      amount: unit - base,
      multiplier,
      additionalCost,
    });
  }

  return { unit, priced: true, flat: false, steps, ...applyTax(unit, taxMode, taxRate) };
}

/** Espeja `applyTax` de driver-payment.util: 'inclusive' desglosa, 'exclusive' suma. */
export function applyTax(sumUnits, taxMode, taxRate) {
  const rate = Number.isFinite(Number(taxRate)) ? Number(taxRate) : 0.19;
  if (taxMode === 'inclusive') {
    const total = Math.round(sumUnits);
    const net = Math.round(total / (1 + rate));
    return { net, tax: total - net, total };
  }
  const net = Math.round(sumUnits);
  const tax = Math.round(net * rate);
  return { net, tax, total: net + tax };
}

/**
 * Comunas que tienen una regla propia en la config, para poblar el selector del
 * simulador. Se devuelve el nombre TAL COMO lo escribió el operador (la normalización
 * es solo para comparar), sin repetidos y en orden alfabético.
 */
export function communesWithRules(zones = []) {
  const seen = new Map();
  for (const z of zones) {
    for (const name of z?.communeNames ?? []) {
      const key = normalizeCommune(name);
      if (key && !seen.has(key)) seen.set(key, name);
    }
  }
  return [...seen.values()].sort((a, b) => String(a).localeCompare(String(b), 'es'));
}
